import React, { useRef, useEffect } from 'react'

import { dataIdFromObject } from '@sketch/graphql-cache'

import {
  DocumentHead,
  ErrorMessage,
  handleFetchMore,
  useBanner,
  LoadingState,
} from '@sketch/components'

import { localStorageKeys } from '@sketch/constants'
import { useFlag, useUserProfile } from '@sketch/modules-common'

import { SKETCH_WEBSITE } from '@sketch/env-config'

import {
  getParsedItem,
  setStringifiedItem,
  useOnEvent,
  useSearch,
} from '@sketch/utils'

import { useCanUseDesignSystemManager } from '../../designSystems/operations/useCanUseDesignSystemManager'

import { useSortingSettings } from '../hooks/useSortSettings'
import { useSearchFiltersOld } from '../hooks/useSearchFiltersOld'

import MfaBanner from '../../../components/MfaBanner'
import { DocumentItemDropdown } from './DocumentItemDropdown'

import { DocumentItemsLayout } from './DocumentItemsLayout'
import ShareEmptyState from './ShareEmptyState'
import { userIsMember } from '../../workspace/utils'

import {
  useGetSharesQuery,
  useGetSharesLibrariesQuery,
  useGetWorkspaceUploadsRestrictedForQuery,
  WorkspaceMinimalFragment,
  GetSharesDocument,
  GetSharesQuery,
  GetSharesQueryVariables,
} from '@sketch/gql-types'

import { UpsellBanner } from '../../designSystems/components/UpsellBanner'
import { ScrollToTop } from '../../../components/ScrollToTopButton'
import {
  storageItemFiltersToShareFilters,
  useStorageItemsFilters,
} from '../../storageItems/hooks'

import { updatePinnedShares } from './ShareActionsDropdown/pinShare.cache'

interface WorkspaceSharesListProps {
  workspaceId: string
  workspace: WorkspaceMinimalFragment
  filter?: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | 'TEMPLATE' | null
  showArchived?: boolean
}

const entriesPath = ['workspace', 'shares', 'entries']

const HEADER_COPY = {
  SHARED_WITH_ME: {
    title: 'Shared with Me',
    description: 'All documents that are directly shared with you.',
    learnMoreTooltipTitle: 'Learn more about Shared with Me',
    learnMoreTooltipURL: `${SKETCH_WEBSITE}/docs/sharing-and-collaborating/sharing-and-viewing-documents/shared-with-me/#how-to-access-shared-with-me`,
  },
  LIBRARY: {
    title: 'Libraries',
    description:
      "Libraries are documents with components that can be shared across other documents. To add a Library, select Use as Library in a document's settings.",
    learnMoreTooltipTitle: 'Learn more about Libraries',
    learnMoreTooltipURL: `${SKETCH_WEBSITE}/docs/libraries/`,
  },
  TEMPLATE: {
    title: 'Templates',
    description:
      'Templates are reusable Workspace documents with pre-populated content.',
    learnMoreTooltipTitle: 'Learn more about Templates',
    learnMoreTooltipURL: `${SKETCH_WEBSITE}/docs/symbols-and-styles/templates/`,
  },
  NO_PROJECT: {
    title: 'All Documents',
    description:
      'All the documents in your Workspace — in sync and available anywhere.',
    learnMoreTooltipTitle: 'Learn more',
    learnMoreTooltipURL: `${SKETCH_WEBSITE}/docs/sharing-and-collaborating/using-your-workspace/viewing-documents/`,
  },
}

const WorkspaceSharesList = ({
  workspaceId,
  filter = null,
  workspace,
  showArchived,
}: WorkspaceSharesListProps) => {
  const isDesignSystemsOn = useFlag('design-systems')

  const isNestedProjectsOn = useFlag('nested-projects')

  const { searchDebounced: search } = useSearch()
  const { sort } = useSortingSettings()

  const workspaceHasDocuments = useRef<boolean>()
  const { showBanner } = useBanner()

  const userProfile = useUserProfile()
  const isVerified = userProfile?.data?.me?.isVerified
  const isMfaEnabled = userProfile?.data?.me?.mfaState === 'ENABLED'
  const isSsoWorkspace = workspace?.customer?.ssoEnabled
  const isArchivedEnabled = workspace.features.archiveEnabled

  // check if the user has permissions to get the design systems - DIFFERENT from the business flag
  const isMember = userIsMember(workspace)
  const canAccessDesignSystems =
    isMember && isDesignSystemsOn && filter === 'LIBRARY'

  const { canUseDesignSystemManager } = useCanUseDesignSystemManager({
    workspace,
  })

  const { filters: filtersOld } = useSearchFiltersOld()

  const filterVariables = storageItemFiltersToShareFilters(
    useStorageItemsFilters().filters
  )

  const filters = isNestedProjectsOn ? filterVariables.filters : filtersOld

  if (filter) {
    filters.push(filter)
  }

  /*
   * We want to show Archived Documents when the user selects the proper filter
   * or when the user is searching for something (with or without the filter enabled)
   */
  const showArchivedDocuments =
    (isArchivedEnabled &&
      (isNestedProjectsOn
        ? filterVariables.showArchivedDocuments
        : showArchived)) ||
    search

  const queryVariables = {
    identifier: workspaceId,
    after: null,
    search: {
      name: search,
      filters,
    },
    sortOrder: sort,
    archived: showArchivedDocuments ? 'ALL' : undefined,
  } satisfies GetSharesQueryVariables

  const resultDocuments = useGetSharesQuery({
    variables: {
      identifier: workspaceId,
      after: null,
      search: {
        name: search,
        filters,
      },
      sortOrder: sort,
      archived: showArchivedDocuments ? 'ALL' : undefined,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: canAccessDesignSystems,
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  // When design systems is removed we could remove this query and simplify
  // the shares retrieval
  // https://linear.app/sketch/issue/SWEB-828/remove-design-systems-and-design-systems-editor-ff-and-logic
  const resultLibrary = useGetSharesLibrariesQuery({
    variables: {
      identifier: workspaceId,
      after: null,
      search: {
        name: search,
        filters,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !canAccessDesignSystems,
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  // libraries need to get the design systems data
  const result = canAccessDesignSystems ? resultLibrary : resultDocuments

  const uploadsRestrictedForResult = useGetWorkspaceUploadsRestrictedForQuery({
    variables: { identifier: workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  useOnEvent('workspaceShareRefresh', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId) {
      // Not the workspace visible ignore then
      return
    }
    result.refetch()
  })

  // We are keeping the totalCount in a ref
  // to see if the Workspace has documents
  // to control Banner logic even if a search
  // returns a totalCount of 0
  useEffect(() => {
    if (!search) {
      const totalCount = result?.data?.workspace?.shares?.meta?.totalCount
      workspaceHasDocuments.current = !!totalCount
    }
  }, [result, search])

  const sharesCount = result.data?.workspace.shares.meta.totalCount ?? 0

  // Mfa Banner to encourage the user to enable it
  useEffect(() => {
    if (!isMfaEnabled && isVerified && sharesCount !== 0 && !isSsoWorkspace) {
      const now = new Date()
      const stopRenderAfter = getParsedItem(localStorageKeys.mfaBanner)

      // if the date wasn't set before, set it
      if (!stopRenderAfter) {
        const oneDayFromNow = new Date(Date.now() + 3600 * 1000 * 24)

        setStringifiedItem(localStorageKeys.mfaBanner, oneDayFromNow)
      }

      if (!(now > new Date(stopRenderAfter))) {
        const { dismissBanner } = showBanner({
          id: 'mfa-banner',
          type: 'custom',
          message: <MfaBanner />,
        })

        return () => {
          dismissBanner()
        }
      }
    }
  }, [isSsoWorkspace, isMfaEnabled, isVerified, sharesCount, showBanner])

  if (!result?.data && result?.error) {
    return <ErrorMessage.Generic />
  }

  const shares = result?.data?.workspace.shares.entries ?? []

  const after = result.data?.workspace.shares.meta.after || null
  const loadMoreHandler = handleFetchMore(result.fetchMore, entriesPath, {
    dataIdFromObject,
    after,
  })

  const loading = result.networkStatus < 3 || uploadsRestrictedForResult.loading
  const totalShareCount = result?.data?.workspace.shares.meta.totalCount || 0
  const headerCopy = filter ? HEADER_COPY[filter] : HEADER_COPY['NO_PROJECT']

  // if we are on libraries, we don't want to show an empty screen straight away
  if (!loading && shares.length === 0) {
    return (
      <>
        {headerCopy && <DocumentHead title={headerCopy.title} />}

        <ShareEmptyState
          filter={filter}
          search={search}
          workspace={workspace}
        />
      </>
    )
  }

  // if library, get the upsell banner
  const showUpsell = !canUseDesignSystemManager && filter === 'LIBRARY'

  if (loading) {
    return <LoadingState />
  }

  return (
    <>
      {headerCopy && <DocumentHead title={headerCopy.title} />}
      {showUpsell && <UpsellBanner />}

      <DocumentItemsLayout
        workspaceId={workspaceId}
        shares={shares}
        search={search}
        renderDropdown={share => {
          return (
            <DocumentItemDropdown
              share={share}
              workspaceIdentifier={workspaceId}
              workspace={workspace}
              pinDocumentConfig={{
                shareIdentifier: share.identifier,
                pinned: !!share?.pinnedByCurrentUserAt,
                update(cache, { data }) {
                  if (!data) return

                  updatePinnedShares<GetSharesQuery>({
                    shareId: share.identifier,
                    pickPagination: data => data?.workspace.shares,
                    readData: () => {
                      const result = cache.readQuery<GetSharesQuery>({
                        query: GetSharesDocument,
                        variables: queryVariables,
                      })
                      return result ?? undefined
                    },
                    writeData: newData => {
                      cache.writeQuery({
                        query: GetSharesDocument,
                        variables: queryVariables,
                        data: newData,
                      })
                    },
                    sort,
                  })
                },
              }}
            />
          )
        }}
        onLoadMore={loadMoreHandler}
        totalCount={totalShareCount}
        showProjectName
        canDropShares={false}
      />

      <ScrollToTop />
    </>
  )
}

export default WorkspaceSharesList
